import { useState, useCallback, useMemo } from 'react';

export const useTablePagination = (
  totalItems: number,
  initialPageSize: number = 10,
  initialPage: number = 1
) => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const pageCount = useMemo(() => Math.ceil(totalItems / pageSize), [totalItems, pageSize]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page >= 1 && page <= pageCount) {
        setCurrentPage(page);
      }
    },
    [pageCount]
  );

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const nextPage = useCallback(() => {
    handlePageChange(currentPage + 1);
  }, [currentPage, handlePageChange]);

  const previousPage = useCallback(() => {
    handlePageChange(currentPage - 1);
  }, [currentPage, handlePageChange]);

  const firstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const lastPage = useCallback(() => {
    setCurrentPage(pageCount);
  }, [pageCount]);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  return {
    currentPage,
    pageSize,
    pageCount,
    startIndex,
    endIndex,
    handlePageChange,
    handlePageSizeChange,
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    hasNextPage: currentPage < pageCount,
    hasPreviousPage: currentPage > 1,
  };
};
